/**
 * Voucher store using Zustand
 */
import { create } from 'zustand';
import { apiService } from '../services/api';
import type {
  Voucher,
  VoucherFilter,
  VoucherStatistics,
} from '../types';

interface VoucherStore {
  vouchers: Voucher[];
  selectedVoucher: Voucher | null;
  statistics: VoucherStatistics | null;
  filters: VoucherFilter;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  error: string | null;

  // Actions
  setFilters: (filters: Partial<VoucherFilter>) => void;
  setPagination: (page: number, pageSize?: number) => void;
  fetchVouchers: () => Promise<void>;
  fetchVoucher: (id: string) => Promise<void>;
  createVoucher: (data: Voucher) => Promise<void>;
  updateVoucher: (id: string, data: Partial<Voucher>) => Promise<void>;
  markVoucherUsed: (id: string, usedDate?: string) => Promise<void>;
  deleteVoucher: (id: string) => Promise<void>;
  uploadEmlFile: (file: File) => Promise<Voucher[]>;
  fetchStatistics: () => Promise<void>;
  clearError: () => void;
  clearSelectedVoucher: () => void;
}

export const useVoucherStore = create<VoucherStore>((set, get) => ({
  vouchers: [],
  selectedVoucher: null,
  statistics: null,
  filters: {},
  pagination: {
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0,
  },
  isLoading: false,
  error: null,

  setFilters: (newFilters: Partial<VoucherFilter>) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
      pagination: { ...state.pagination, page: 1 }, // Reset to first page on filter change
    }));
  },

  setPagination: (page: number, pageSize?: number) => {
    set((state) => ({
      pagination: {
        ...state.pagination,
        page,
        ...(pageSize && { pageSize }),
      },
    }));
  },

  fetchVouchers: async () => {
    set({ isLoading: true, error: null });
    try {
      const { filters, pagination } = get();
      const response = await apiService.getVouchers(
        filters,
        pagination.page,
        pagination.pageSize
      );
      set({
        vouchers: response.vouchers,
        pagination: {
          page: response.page,
          pageSize: response.page_size,
          total: response.total,
          totalPages: response.total_pages,
        },
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch vouchers',
        isLoading: false,
      });
    }
  },

  fetchVoucher: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const voucher = await apiService.getVoucher(id);
      set({ selectedVoucher: voucher, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch voucher',
        isLoading: false,
      });
    }
  },

  createVoucher: async (data: Voucher) => {
    set({ isLoading: true, error: null });
    try {
      await apiService.createVoucher(data);
      await get().fetchVouchers();
      await get().fetchStatistics();
      set({ isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create voucher',
        isLoading: false,
      });
      throw error;
    }
  },

  updateVoucher: async (id: string, data: Partial<Voucher>) => {
    set({ isLoading: true, error: null });
    try {
      await apiService.updateVoucher(id, data);
      await get().fetchVouchers();
      await get().fetchStatistics();
      if (get().selectedVoucher?.id === id) {
        await get().fetchVoucher(id);
      }
      set({ isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update voucher',
        isLoading: false,
      });
      throw error;
    }
  },

  markVoucherUsed: async (id: string, usedDate?: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiService.markVoucherUsed(id, usedDate);
      await get().fetchVouchers();
      await get().fetchStatistics();
      if (get().selectedVoucher?.id === id) {
        await get().fetchVoucher(id);
      }
      set({ isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to mark voucher as used',
        isLoading: false,
      });
      throw error;
    }
  },

  deleteVoucher: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiService.deleteVoucher(id);
      await get().fetchVouchers();
      await get().fetchStatistics();
      if (get().selectedVoucher?.id === id) {
        set({ selectedVoucher: null });
      }
      set({ isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete voucher',
        isLoading: false,
      });
      throw error;
    }
  },

  uploadEmlFile: async (file: File) => {
    set({ isLoading: true, error: null });
    try {
      const vouchers = await apiService.uploadEmlFile(file);
      await get().fetchVouchers();
      await get().fetchStatistics();
      set({ isLoading: false });
      return vouchers;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to upload file',
        isLoading: false,
      });
      throw error;
    }
  },

  fetchStatistics: async () => {
    try {
      const statistics = await apiService.getStatistics();
      set({ statistics });
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  },

  clearError: () => set({ error: null }),
  clearSelectedVoucher: () => set({ selectedVoucher: null }),
}));

